/**
 * PersonalityTab
 *
 * Admin UI for the personality_hint + personality_sink hooks.
 * Dynamically reads registered signal types + sources from the backend so
 * adding a new PersonalitySignalType requires zero frontend edits.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { Fingerprint, ArrowDownToLine } from 'lucide-react';
import { getConfigValue } from '@/config/fieldConfig';
import { baseApiClient } from '@/lib/api/client';
import type { AgentConfiguration } from '@/types';

interface SignalTypeMeta {
  value: string;
  display_zh: string;
  display_en: string;
  description_zh: string;
  description_en: string;
  payload_schema: Record<string, unknown>;
}

interface SignalSourceMeta {
  value: string;
  display_zh: string;
  display_en: string;
  priority: number;
}

interface SignalTypesResponse {
  types: SignalTypeMeta[];
  sources: SignalSourceMeta[];
}

interface PersonalityTabProps {
  config: Partial<AgentConfiguration>;
  onChange: (name: string, value: unknown) => void;
  appContext: unknown;
}

function Toggle({
  on,
  onClick,
  label,
  description,
  icon: Icon,
  accent,
}: {
  on: boolean;
  onClick: () => void;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: 'indigo' | 'sky';
}) {
  const accentBg = accent === 'indigo' ? 'bg-indigo-100' : 'bg-sky-100';
  const accentFg = accent === 'indigo' ? 'text-indigo-600' : 'text-sky-600';
  const accentOn = accent === 'indigo' ? 'bg-indigo-500' : 'bg-sky-500';
  return (
    <Card className="p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-xl ${on ? accentBg : 'bg-slate-100'}`}>
            <Icon className={`w-6 h-6 ${on ? accentFg : 'text-slate-400'}`} />
          </div>
          <div>
            <h3 className="font-medium text-slate-900">{label}</h3>
            <p className="text-sm text-slate-500 mt-1">{description}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onClick}
          className={`relative w-14 h-7 rounded-full transition-colors flex-shrink-0 ${
            on ? accentOn : 'bg-slate-300'
          }`}
        >
          <div
            className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              on ? 'translate-x-8' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </Card>
  );
}

export const PersonalityTab: React.FC<PersonalityTabProps> = ({ config, onChange }) => {
  const sinkEnabled = getConfigValue(config, 'hooks.personality_sink.enabled') !== false;
  const hintEnabled = getConfigValue(config, 'hooks.personality_hint.enabled') === true;
  const enabledTypes = (getConfigValue(config, 'hooks.personality_hint.enabled_types') as string[] | undefined) ?? [];
  const sourceFilter = (getConfigValue(config, 'hooks.personality_hint.source_filter') as string[] | undefined) ?? [];
  const recencyDays = (getConfigValue(config, 'hooks.personality_hint.recency_days_window') as number | undefined) ?? 180;
  const maxSignals = (getConfigValue(config, 'hooks.personality_hint.max_signals') as number | undefined) ?? 5;
  const confidenceThreshold =
    (getConfigValue(config, 'hooks.personality_hint.confidence_threshold') as number | undefined) ?? 0;
  const customTemplate = (getConfigValue(config, 'hooks.personality_hint.custom_template') as string | null | undefined) ?? '';

  const { data: meta, isLoading, error } = useQuery<SignalTypesResponse>({
    queryKey: ['personality-signal-types'],
    queryFn: async () => {
      const resp = await baseApiClient.get<SignalTypesResponse>('/v1/admin/personality-signals/types');
      return resp.data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: hintEnabled,
  });

  const toggleInList = (listPath: string, current: string[], value: string) => {
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    onChange(listPath, next);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">人格画像</h2>
        <p className="text-sm text-slate-500 mt-1">
          采集并注入 MBTI、SBTI 等多源人格信号。注入内容明确标注"非定论，仅供语气参考"。
        </p>
      </div>

      <Toggle
        on={sinkEnabled}
        onClick={() => onChange('hooks.personality_sink.enabled', !sinkEnabled)}
        label="小程序结果采集"
        description="检测 [mini-app result] 用户消息，把 SBTI 等小程序结果持久化到 astro_profile。关闭后结果只在当轮对话可见。"
        icon={ArrowDownToLine}
        accent="sky"
      />

      <Toggle
        on={hintEnabled}
        onClick={() => onChange('hooks.personality_hint.enabled', !hintEnabled)}
        label="人格画像注入"
        description="在 System Prompt 中注入用户的 MBTI/SBTI/... 线索（带来源和信度），默认关闭。"
        icon={Fingerprint}
        accent="indigo"
      />

      {hintEnabled && (
        <Card className="p-6 space-y-6">
          {isLoading && <p className="text-sm text-slate-500">加载类型元数据中...</p>}
          {error && (
            <p className="text-sm text-red-600">加载失败: {(error as Error).message}</p>
          )}

          {meta && (
            <>
              <section>
                <h4 className="text-sm font-semibold text-slate-800 mb-2">信号类型白名单</h4>
                <p className="text-xs text-slate-500 mb-3">
                  只注入勾选类型的信号。新增类型只要后端注册 enum，此处会自动出现。
                </p>
                <div className="flex flex-wrap gap-2">
                  {meta.types.map((t) => {
                    const checked = enabledTypes.includes(t.value);
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => toggleInList('hooks.personality_hint.enabled_types', enabledTypes, t.value)}
                        className={`px-3 py-1.5 rounded-lg border text-sm transition ${
                          checked
                            ? 'bg-indigo-500 text-white border-indigo-500'
                            : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-400'
                        }`}
                        title={t.description_zh}
                      >
                        {t.display_zh}
                      </button>
                    );
                  })}
                </div>
              </section>

              <section>
                <h4 className="text-sm font-semibold text-slate-800 mb-2">来源过滤</h4>
                <p className="text-xs text-slate-500 mb-3">
                  空选 = 不过滤（全部来源参与聚合）。勾选后仅使用选定来源的信号。优先级数字越大权重越高。
                </p>
                <div className="flex flex-wrap gap-2">
                  {meta.sources.map((s) => {
                    const checked = sourceFilter.includes(s.value);
                    return (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => toggleInList('hooks.personality_hint.source_filter', sourceFilter, s.value)}
                        className={`px-3 py-1.5 rounded-lg border text-sm transition ${
                          checked
                            ? 'bg-emerald-500 text-white border-emerald-500'
                            : 'bg-white text-slate-700 border-slate-300 hover:border-emerald-400'
                        }`}
                      >
                        {s.display_zh}
                        <span className="ml-1 text-xs opacity-70">({s.priority})</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="block">
              <span className="text-sm font-medium text-slate-700">近期窗口（天）</span>
              <input
                type="number"
                min={0}
                max={3650}
                value={recencyDays}
                onChange={(e) => onChange('hooks.personality_hint.recency_days_window', Number(e.target.value))}
                className="mt-1 w-full rounded border-slate-300 text-sm"
              />
              <span className="text-xs text-slate-500">0 表示不限</span>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">最大注入条数</span>
              <input
                type="number"
                min={1}
                max={20}
                value={maxSignals}
                onChange={(e) => onChange('hooks.personality_hint.max_signals', Number(e.target.value))}
                className="mt-1 w-full rounded border-slate-300 text-sm"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-700">置信度阈值 (0-1)</span>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={confidenceThreshold}
                onChange={(e) => onChange('hooks.personality_hint.confidence_threshold', Number(e.target.value))}
                className="mt-1 w-full rounded border-slate-300 text-sm"
              />
              <span className="text-xs text-slate-500">仅影响 AI 推断信号</span>
            </label>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">自定义 Jinja2 模板（留空用默认）</span>
            <textarea
              rows={8}
              value={customTemplate ?? ''}
              onChange={(e) => onChange('hooks.personality_hint.custom_template', e.target.value || null)}
              placeholder="留空使用默认模板：'用户人格画像线索（多源混合，仅供语气与偏好参考，非定论）...'"
              className="mt-1 w-full rounded border-slate-300 text-xs font-mono"
            />
            <span className="text-xs text-slate-500">
              可用变量: signals[].type / type_display / source / source_display / summary / confidence_pct / age_days
            </span>
          </label>
        </Card>
      )}
    </div>
  );
};

export default PersonalityTab;
